/**
 * projection.js — Year-by-year financial projection engine.
 * Pure function — zero React.
 */
import { taxCA } from './tax';
import { mp, pv } from './mortgage';
import { FHSA_ANNUAL, FHSA_LIFETIME, SWR_DEFAULT, PENSION_PAYOUT_YRS } from '../constants/financial';

export function run(s) {
  const d = [];
  let fhsa = 0, tfsa = 0, rrsp = 0, nr = s.nw0 || 0;
  let tRoom = s.tfsaRoom || 95000;
  let mBal = s.mortAmt;
  const annualMort = s.mortAmt > 0 ? mp(s.mortAmt, s.mortRate, s.amortYrs) * 12 : 0;
  const mr = s.mortRate / 12;
  const mpay = s.mortAmt > 0 ? mp(s.mortAmt, s.mortRate, s.amortYrs) : 0;
  let pYrs = 0, salHistory = [], fhsaContrib = 0, fhsaRoom = FHSA_ANNUAL;
  const inf = s.inflation != null ? s.inflation : 0.025;
  const maxA = s.maxAge || 40;
  const penAge = s.penAge || 60;
  let reachedFI = false;

  const effectiveLiv = s.budgetSync && s.cats
    ? s.cats.reduce((a, x) => a + (x.a || 0), 0) * 12
    : s.liv;

  for (let a = s.age; a <= maxA; a++) {
    const working = a >= s.startAge;
    let base = 0;
    if (working) {
      const certBoost = (s.cPMP ? 0.05 : 0) + (s.cAWS ? 0.05 : 0) + (s.cCISSP ? 0.05 : 0);
      base = s.salary * Math.pow(1 + s.salGr, a - s.startAge) * (1 + certBoost);
    }
    const bon = working ? base * s.bonusR : 0;
    const comp = base + bon;

    const livAdj = effectiveLiv * (inf > 0 ? Math.pow(1 + inf, a - s.age) : 1);
    const mortgage = a >= s.hAge && mBal > 0 ? annualMort : 0;

    let svTarget = a < 25 ? s.s22 : a < 30 ? s.s25 : a < 35 ? s.s30 : s.s35;
    if (!working) svTarget = Math.min(svTarget, 15000);

    const estRRSP = Math.min(Math.max(0, svTarget * 0.3), comp * 0.18);
    const taxObj = taxCA(Math.max(0, comp - estRRSP));
    const afterTax1 = comp - taxObj.total;

    const prevNW = d.length > 0 ? d[d.length - 1].nw : s.nw0;
    if (prevNW * SWR_DEFAULT >= livAdj) reachedFI = true;
    const maxSave = afterTax1 - livAdj - mortgage;
    let sv = reachedFI ? 0 : Math.max(0, Math.min(svTarget, maxSave));

    const fhsaCap = Math.min(fhsaRoom, Math.max(0, FHSA_LIFETIME - fhsaContrib), sv);
    fhsaContrib += fhsaCap;
    const unusedFhsa = fhsaRoom - fhsaCap;
    fhsaRoom = Math.min(FHSA_ANNUAL + Math.min(unusedFhsa, FHSA_ANNUAL), FHSA_LIFETIME - fhsaContrib);
    if (fhsaContrib >= FHSA_LIFETIME) fhsaRoom = 0;

    let rem = sv - fhsaCap;
    const tfsaCap = Math.min(s.tLim, Math.max(0, tRoom), rem);
    rem -= tfsaCap;
    const rrspPct = (s.rrspPct != null ? s.rrspPct : 60) / 100;
    const rrspCap = Math.min(Math.max(0, rem * rrspPct), comp * 0.18);
    rem -= rrspCap;
    const nrCap = Math.max(0, rem);

    const taxFinal = taxCA(Math.max(0, comp - rrspCap));
    const afterTax = comp - taxFinal.total;
    const taxRate = comp > 0 ? Math.round((taxFinal.total / comp) * 100) : 0;
    const margRate = comp > 0 ? Math.round(
      ((taxCA(comp).total - taxCA(Math.max(0, comp - 1000)).total) / 1000) * 100
    ) : 0;

    const eqAlloc = (s.xeqt != null ? s.xeqt : 100) / 100;
    const r = eqAlloc * s.ret + (1 - eqAlloc) * 0.04;

    fhsa = (fhsa + fhsaCap) * (1 + r);
    tfsa = (tfsa + tfsaCap) * (1 + r);
    rrsp = (rrsp + rrspCap) * (1 + r);
    nr = (nr + nrCap) * (1 + r);
    tRoom = Math.max(0, tRoom - tfsaCap + s.tLim);

    const liq = fhsa + tfsa + rrsp + nr;
    let eq = 0, hv = s.hPrice;
    if (a >= s.hAge) {
      hv = s.hPrice * Math.pow(1 + s.hApp, a - s.hAge);
      if (mBal > 0) {
        for (let m = 0; m < 12 && mBal > 0; m++) mBal = Math.max(0, mBal - (mpay - mBal * mr));
        mBal = Math.max(0, mBal - s.xPrin);
      }
      eq = Math.max(0, hv - mBal);
    }

    if (working) { pYrs++; salHistory.push(base); }
    const best5 = [...salHistory].sort((a, b) => b - a).slice(0, 5);
    const avg5 = best5.length ? best5.reduce((a, b) => a + b, 0) / best5.length : 0;
    const pensionAnnual = 0.02 * pYrs * avg5;
    const discR = 0.03;
    const annuityFactor = discR > 0 ? (1 - Math.pow(1 + discR, -PENSION_PAYOUT_YRS)) / discR : PENSION_PAYOUT_YRS;
    const spousalAdj = s.spousal ? 0.90 : 1.0;
    const pensionPV = pensionAnnual > 0 ? pv(pensionAnnual * annuityFactor * spousalAdj, discR, Math.max(0, penAge - a)) : 0;

    const nw = liq + eq + pensionPV;
    const surplus = afterTax - livAdj - mortgage - sv;
    const estRefund = Math.max(0, taxCA(comp).total - taxFinal.total);

    d.push({
      a, base: Math.round(base), bon: Math.round(bon), comp: Math.round(comp),
      afterTax: Math.round(afterTax), taxRate, margRate,
      taxFed: Math.round(taxFinal.fed), taxProv: Math.round(taxFinal.prov),
      taxCPP: Math.round(taxFinal.cpp), taxEI: Math.round(taxFinal.ei),
      sv, fhsa: Math.round(fhsa), tfsa: Math.round(tfsa), rrsp: Math.round(rrsp),
      nr: Math.round(nr), liq: Math.round(liq), hv: Math.round(hv), eq: Math.round(eq),
      mBal: Math.round(mBal), moP: Math.round(annualMort / 12), pYrs,
      av5: Math.round(avg5), pA: Math.round(pensionAnnual), pPV: Math.round(pensionPV),
      nw: Math.round(nw), fi: Math.round(nw * SWR_DEFAULT), liv: Math.round(livAdj),
      sur: Math.round(surplus), postFI: reachedFI, estRefund: Math.round(estRefund),
    });
  }
  return d;
}
